/* eslint-disable react/no-unstable-nested-components */
import { faPen, faPlus, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons"
import Select from "react-select"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import React, { useEffect, useState } from "react"
import { Modal, Pagination, TextInput, Loading, Search } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import {
  useGetCommoditiesQuery,
  useGetCategoriesQuery,
  useAddCommoditiesMutation,
  useUpdateCommoditiesMutation,
  useDeleteCommoditiesMutation,
} from "services/core/commodities"
import { Commodities } from "interfaces/dataInterfaces"
import { GET_ITEM_LIMIT, MARKET_MODULE } from "constants/constants"
import useCommon from "hooks/useCommon"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"

type Inputs = {
  name: string
  extendedName: string
  CategoryName: string
  category: string
}
const permissionsProps = {
  moduleId: MARKET_MODULE,
  subModule: "ccpucs",
}
export default function CommoditiesPage() {
  const { showSnackBar } = useCommon()
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setValue,
  } = useForm<Inputs>()

  const [actionItem, setActionItem] = useState<{
    item: Commodities
    action: "edit" | "delete"
  } | null>(null)

  const [query, setQuery] = useState<string | undefined>("")
  const [showNewCommodityModal, setShowNewCommodityModal] = useState(false)
  const [inputText, setInputText] = useState<string | undefined>(undefined)
  const [pageNumber, setPageNumber] = useState(1)
  const [getItemLimit, setGetItemLimit] = useState(GET_ITEM_LIMIT)

  const { data: CommoditiesData, isFetching: isLoadingCommodities } = useGetCommoditiesQuery({
    skip: pageNumber === 1 ? 0 : (pageNumber - 1) * getItemLimit,
    take: getItemLimit,
    query,
  })

  const { data: categories, isFetching: isLoadingCategories } = useGetCategoriesQuery({
    skip: 0,
    take: 0,
    query: undefined,
  })
  const [addCommodities, { isLoading: isLoadingAdd }] = useAddCommoditiesMutation()
  const [updateCommodities, { isLoading: isLoadingEdit }] = useUpdateCommoditiesMutation()
  const [deleteCommodities, { isLoading: isLoadingDelete }] = useDeleteCommoditiesMutation()

  const categoryData: Array<{ value: number; label: string }> = []
  categories?.items.map((c: any) => {
    categoryData.push({
      value: c.commodityCategoryID,
      label: c.commodityCategoryName,
    })
  })

  const onSubmit: SubmitHandler<Inputs> = async data => {
    if (actionItem?.action === "edit") {
      const categoryids = categoryData.find(r => r.label === data.category)
      try {
        await updateCommodities({
          // @ts-ignore
          commodityID: actionItem.item.commodityId,
          commodityName: data.name,
          commodityCategoryID: categoryids?.value,
        }).unwrap()
        setActionItem(null)
        showSnackBar("Commodity updated successfully!")
      } catch (err) {
        showSnackBar("Commodity not updated", "error")
      }
    } else {
      const categoryids = categoryData.find(r => r.label === data.category)
      try {
        // @ts-ignore
        await addCommodities({ commodityName: data.name, commodityCategoryID: categoryids?.value }).unwrap()
        setShowNewCommodityModal(false)
        showSnackBar("Commodity added successfully!")
      } catch (err) {
        showSnackBar("Commodity not added", "error")
      }
    }
    reset()
  }

  useEffect(() => {
    reset()
    if (actionItem?.action === "edit") {
      setValue("category", actionItem?.item.commodityCategoryName)
    }
  }, [actionItem])

  const columns: Array<ColumnShape<Commodities>> = [
    {
      key: "commodityId",
      dataKey: "commodityId",
      title: "ID",
      width: 80,
      maxWidth: 80,
    },
    {
      key: "commodityCategoryName",
      dataKey: "commodityCategoryName",
      title: " Category ",
      width: 400,
    },
    {
      key: "commodityName",
      dataKey: "commodityName",
      title: "Name",
      width: 350,
    },
    {
      key: "action",
      width: 250,
      align: Column.Alignment.CENTER,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => (
        <PermissionWrapper {...permissionsProps} feature="edit">
          <>
            <Button
              action={() =>
                setActionItem({
                  item: rowData,
                  action: "edit",
                })
              }
              label="Edit"
              className="btn-full blue action-btn"
              icon={faPen}
            />

            <Button
              action={() =>
                setActionItem({
                  item: rowData,
                  action: "delete",
                })
              }
              label="Delete"
              className="btn-full danger action-btn"
              style={{ marginLeft: "10px" }}
              icon={faTrash}
            />
          </>
        </PermissionWrapper>
      ),
    },
  ]

  const handleDelete = async (id: number) => {
    try {
      await deleteCommodities(id).unwrap()
      setActionItem(null)
      showSnackBar("Commodity deleted successfully!")
    } catch (error) {
      showSnackBar("Commodity not deleted", "error")
    }
  }

  const handleNewEditModalClose = () => {
    reset()
    actionItem ? setActionItem(null) : setShowNewCommodityModal(false)
  }

  const handleSearch = (e: any) => {
    setInputText(e)
  }

  const handleSubmitQuery = () => {
    setPageNumber(1)
    setQuery(inputText)
  }

  const handleSearchKey = (e: any) => {
    inputText && e?.code === "Enter" && handleSubmitQuery()
  }

  useEffect(() => {
    !inputText && query && setQuery("")
  }, [inputText])

  return (
    <>
      {(isLoadingAdd || isLoadingEdit || isLoadingDelete) && <Loading />}
      <HeaderFilters>
        <div className="d-flex" style={{ width: "300px" }}>
          <Search
            id="query"
            name="query"
            placeholder="Type here to filter results..."
            className="text-input mr-2"
            value={query || ""}
            onChange={e => handleSearch(e)}
            onKeyUp={e => handleSearchKey(e)}
          />
          <Button action={handleSubmitQuery} label="Search" className="btn-full blue" icon={faSearch} />
        </div>

        <PermissionWrapper {...permissionsProps} feature="addNew">
          <Button
            action={() => setShowNewCommodityModal(true)}
            label="Add new commodity"
            className="btn-full green"
            icon={faPlus}
          />
        </PermissionWrapper>
      </HeaderFilters>

      <div
        className={isLoadingCommodities ? "mt-2 d-flex align-center justify-center" : "mt-2"}
        style={{ height: "100%", minHeight: "60vh" }}
      >
        {isLoadingCommodities ? (
          <Loading withOverlay={false} />
        ) : (
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={CommoditiesData?.items}
                width={width}
                height={height}
                rowKey="commodityId"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        )}
      </div>
      <Modal
        open={actionItem?.action === "delete"}
        type="danger"
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleDelete(actionItem!.item.commodityId)}
        // onSecondarySubmit={() => setActionItem(null)}
        onRequestClose={() => setActionItem(null)}
      >
        Are you sure you want to delete the selected record?
      </Modal>

      <Modal
        open={showNewCommodityModal || actionItem?.action === "edit"}
        modalHeading={actionItem?.action === "edit" ? "Modify commodity" : "Insert new commodity"}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSubmit(onSubmit)}
        onRequestClose={handleNewEditModalClose}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            labelText="Name"
            placeholder="Commodity Name"
            defaultValue={actionItem?.item.commodityName}
            invalid={errors.name}
            invalidText={errors.name?.message}
            {...register("name", { required: "Name is required" })}
          />
          <div className="mt-3">
            <label htmlFor="category" className="wfp--label">
              Category
            </label>
            <Controller
              control={control}
              name="category"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  isLoading={isLoadingCategories}
                  className="wfp--react-select-container"
                  classNamePrefix="wfp--react-select"
                  ref={ref}
                  options={categoryData}
                  value={value ? categoryData.find(r => r.label === value) : null}
                  onChange={item => onChange(item?.label as any)}
                />
              )}
            />
          </div>
        </form>
      </Modal>

      <Pagination
        totalItems={CommoditiesData?.count ?? 0}
        pageSize={getItemLimit}
        pageSizes={[10, 20, 30]}
        page={pageNumber}
        onChange={(event: any): void => {
          setPageNumber((event as any).page)
          setGetItemLimit((event as any).pageSize)
        }}
      />
    </>
  )
}
